import React, { useState } from 'react';
import {
  Box, Container, Grid, Paper, Tab, Tabs, Typography, Table, TableHead,
  TableBody, TableRow, TableCell, Alert, Skeleton,
} from '@mui/material';
import { TrendingUp, VideoLibrary, People } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useUrlIntState } from '../hooks/useUrlState';
import {
  AreaChart, Area, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { tiktokAPI } from '../services/api';
import SectionTitle from './crt/SectionTitle';
import Panel from './crt/Panel';
import ReleaseCalendar, { CalendarEvent, LegendEntry } from './ReleaseCalendar';

const TT_CAL_LEGEND: LegendEntry[] = [
  { type: 'tiktok_video', label: 'TikTok video', color: '#00ffff' },
];

const TikTokCalendarSection: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['tiktok-calendar'],
    queryFn: () => tiktokAPI.getCalendar(12),
    staleTime: 5 * 60_000,
  });
  const events: CalendarEvent[] = (data?.events || []).map((e: any) => ({
    date: e.date,
    title: e.title,
    color: '#00ffff',
    type: e.type,
    url: e.url || undefined,
  }));
  if (!events.length) return null;
  return (
    <ReleaseCalendar
      heading={`> ${events.length} clips posted in the last 12 months`}
      events={events}
      legend={TT_CAL_LEGEND}
    />
  );
};

const fmt = (n: number | null | undefined) => (n ?? 0).toLocaleString();
const pct = (n: number | null | undefined) =>
  n == null ? '—' : `${(n * 100).toFixed(1)}%`;

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div role="tabpanel" hidden={value !== index}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const TikTokDashboard: React.FC = () => {
  const [active, setActive] = useUrlIntState('tt_tab', 0);

  const { data: overview } = useQuery({ queryKey: ['tiktok-overview'], queryFn: tiktokAPI.getOverview, staleTime: 60000 });
  const { data: history } = useQuery({ queryKey: ['tiktok-history'], queryFn: () => tiktokAPI.getHistory(90), staleTime: 60000 });
  const { data: videos } = useQuery({ queryKey: ['tiktok-videos'], queryFn: () => tiktokAPI.getVideos(50), staleTime: 60000 });
  const { data: demographics } = useQuery({ queryKey: ['tiktok-demographics'], queryFn: tiktokAPI.getDemographics, staleTime: 60000 });
  const { data: content } = useQuery({ queryKey: ['tiktok-content'], queryFn: () => tiktokAPI.getContent(100), staleTime: 60000 });

  if (overview && !overview.configured) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box mb={4}>
          <SectionTitle sub="&gt; Follower growth, video performance, engagement_">
            TIKTOK :: VIDEO CONSOLE
          </SectionTitle>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Configure TIKTOK_ACCESS_TOKEN to start syncing TikTok data.
        </Alert>
      </Container>
    );
  }

  const gender = demographics?.dimensions?.gender ?? [];
  const countries = demographics?.dimensions?.country ?? [];
  const hours = demographics?.dimensions?.online_hour ?? [];

  // Merge studio per-video metrics (retention, completion) into the Display
  // API video list. Display API is keyed by numeric video id; studio content
  // is keyed the same way, so a simple map lookup is enough.
  const contentById: Record<string, any> = {};
  (content?.videos || []).forEach((c: any) => { contentById[c.video_id] = c; });

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box mb={4}>
        <SectionTitle sub="&gt; Follower growth, video performance, engagement_">
          TIKTOK :: VIDEO CONSOLE
        </SectionTitle>
      </Box>

      {/* KPI row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: 'FOLLOWERS',      value: fmt(overview?.follower_count) },
          { label: 'VIDEOS',         value: fmt(overview?.video_count) },
          { label: 'TOTAL VIEWS',    value: fmt(overview?.total_views) },
          { label: 'TOTAL LIKES',    value: fmt(overview?.likes_count) },
          { label: 'TOTAL COMMENTS', value: fmt(overview?.total_comments) },
          { label: 'TOTAL SHARES',   value: fmt(overview?.total_shares) },
        ].map((kpi) => (
          <Grid size={{ xs: 6, md: 2 }} key={kpi.label}>
            <Panel label={kpi.label}>
              <Typography variant="h4" fontWeight={700}>{kpi.value}</Typography>
            </Panel>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={active} onChange={(_, v) => setActive(v)} variant="scrollable" scrollButtons="auto" sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab icon={<TrendingUp />} label="Growth" />
          <Tab icon={<VideoLibrary />} label="Videos" />
          <Tab icon={<People />} label="Audience" />
        </Tabs>
      </Paper>

      {/* Growth tab — follower chart + daily engagement from studio data */}
      <TabPanel value={active} index={0}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <Panel label="FOLLOWER TRAJECTORY">
              {history?.available ? (
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={history.daily} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(v: number) => fmt(v)} />
                    <Area type="monotone" dataKey="follower_count" stroke="#00ff41" fill="#00ff41" fillOpacity={0.2} name="Followers" />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={300} />}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel label="DAILY VIEWS">
              {history?.available ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={history.daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(v: number) => fmt(v)} />
                    <Line type="monotone" dataKey="video_views" stroke="#00ffff" name="Video views" dot={false} />
                    <Line type="monotone" dataKey="profile_views" stroke="#ffb000" name="Profile views" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={240} />}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel label="DAILY ENGAGEMENT">
              {history?.available ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={history.daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(v: number) => fmt(v)} />
                    <Line type="monotone" dataKey="daily_likes" stroke="#ff6600" name="Likes" dot={false} />
                    <Line type="monotone" dataKey="daily_comments" stroke="#00ff41" name="Comments" dot={false} />
                    <Line type="monotone" dataKey="daily_shares" stroke="#ff3344" name="Shares" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={240} />}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel label="UNIQUE VIEWERS (daily)">
              {history?.available ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={history.daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(v: number) => fmt(v)} />
                    <Line type="monotone" dataKey="total_viewers" stroke="#00ffff" name="Total" dot={false} />
                    <Line type="monotone" dataKey="new_viewers" stroke="#00ff41" name="New" dot={false} />
                    <Line type="monotone" dataKey="returning_viewers" stroke="#ff6600" name="Returning" dot={false} />
                    <Legend />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={240} />}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Panel label="AVG WATCH TIME (sec, daily)">
              {history?.available ? (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={history.daily}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip formatter={(v: number) => (v == null ? '—' : `${Number(v).toFixed(1)}s`)} />
                    <Line type="monotone" dataKey="avg_play_time_s" stroke="#ffb000" name="Avg play time" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <Skeleton variant="rectangular" height={240} />}
            </Panel>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Videos tab — Display API basics + Studio retention/completion */}
      <TabPanel value={active} index={1}>
        <Panel label="VIDEO CATALOG">
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>TITLE</TableCell>
                  <TableCell>POSTED</TableCell>
                  <TableCell align="right">VIEWS</TableCell>
                  <TableCell align="right">LIKES</TableCell>
                  <TableCell align="right">COMMENTS</TableCell>
                  <TableCell align="right">SHARES</TableCell>
                  <TableCell align="right">AVG WATCH</TableCell>
                  <TableCell align="right">COMPLETION</TableCell>
                  <TableCell align="right">FULL WATCH</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(videos?.videos || []).map((v: any) => {
                  const s = contentById[v.id] || {};
                  return (
                    <TableRow key={v.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 380 }}>
                          {v.title || v.description?.substring(0, 60) || 'Untitled'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{v.create_time ? new Date(v.create_time).toLocaleDateString() : '—'}</TableCell>
                      <TableCell align="right">{fmt(v.view_count)}</TableCell>
                      <TableCell align="right">{fmt(v.like_count)}</TableCell>
                      <TableCell align="right">{fmt(v.comment_count)}</TableCell>
                      <TableCell align="right">{fmt(v.share_count)}</TableCell>
                      <TableCell align="right">{s.avg_watch_time_s != null ? `${Number(s.avg_watch_time_s).toFixed(1)}s` : '—'}</TableCell>
                      <TableCell align="right">{pct(s.completion_rate)}</TableCell>
                      <TableCell align="right">{pct(s.full_video_rate)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Panel>
      </TabPanel>

      {/* Audience tab — gender + top countries + online activity (Studio) */}
      <TabPanel value={active} index={2}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <Panel label="GENDER SPLIT">
              {gender.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={gender} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={pct} />
                    <YAxis type="category" dataKey="bucket" />
                    <Tooltip formatter={(v: number) => pct(v)} />
                    <Bar dataKey="value" fill="#00ff41" name="% of followers" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Typography variant="body2" color="text.secondary">No data</Typography>}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <Panel label="TOP COUNTRIES">
              {countries.length ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={countries.slice(0, 10)} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" tickFormatter={pct} />
                    <YAxis type="category" dataKey="bucket" width={120} />
                    <Tooltip formatter={(v: number) => pct(v)} />
                    <Bar dataKey="value" fill="#00ffff" name="% of followers" />
                  </BarChart>
                </ResponsiveContainer>
              ) : <Typography variant="body2" color="text.secondary">No data</Typography>}
            </Panel>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Panel label="FOLLOWER ONLINE ACTIVITY (active followers by weekday × hour)">
              {hours.length ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small" sx={{ minWidth: 800 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>WEEKDAY × HOUR</TableCell>
                        <TableCell align="right">ACTIVE</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {hours.slice(0, 30).map((h: any, i: number) => (
                        <TableRow key={i} hover>
                          <TableCell><Typography variant="body2">{h.bucket}</Typography></TableCell>
                          <TableCell align="right">{fmt(h.value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : <Typography variant="body2" color="text.secondary">No data</Typography>}
            </Panel>
          </Grid>
        </Grid>
      </TabPanel>
    </Container>
  );
};

export default TikTokDashboard;
